/**
 * The device's local store — what the app reads from when there is no signal.
 *
 * IndexedDB rather than localStorage: a technician's working set is a few
 * hundred records with indexes worth having, and localStorage is a synchronous
 * string map that blocks the main thread and caps out around five megabytes.
 *
 * One object store per syncable entity, keyed by the record's own id, because
 * ids are client-minted UUIDs and never reassigned (D2). That is what lets a
 * record created in a comms room be the same record after it syncs, rather
 * than needing a reconciliation step that always leaks.
 *
 * Two stores are not entities:
 *
 *  * `meta` — the sync cursor, when the device last synced, and the local
 *    sequence behind provisional numbers.
 *  * `outbox` — changes made here that the server has not accepted yet. Keyed
 *    by an auto-incrementing sequence so the queue drains in the order the
 *    tech did the work.
 */

/**
 * Mirrors `APPLY_ORDER` in `app/services/working_set.py`, and for the same
 * reason: a job has to be stored before its lines, both on the way down and on
 * the way back up. There is a test that fails if the two lists drift.
 */
export const ENTITIES = [
  "organisation",
  "user_account",
  "credential",
  "customer",
  "site",
  "contact",
  "asset_category",
  "asset",
  "asset_event",
  "catalogue_item",
  "kit",
  "kit_line",
  "contract",
  "form_template",
  "form_submission",
  "stock_location",
  "stock_level",
  "job",
  "job_line",
  "time_entry",
  "variation",
  "note",
  "attachment",
  "quote",
  "quote_section",
  "quote_line",
] as const;

export type Entity = (typeof ENTITIES)[number];

export const META = "meta";
export const OUTBOX = "outbox";

const DB_NAME = "keystone";
/**
 * Bump when `INDEXES` gains an entry. The upgrade below adds any index that is
 * missing from a store that already exists, so a phone installed before the
 * index was declared gets it too — without this, a new index only ever
 * appears on fresh installs and every existing device quietly scans.
 *
 * 2: `asset.installed_by_job_id`, for the install worklist (ASSET-3).
 */
const DB_VERSION = 2;

/** A stored record. Everything else is whatever the server sent. */
export type Row = { id: string; deleted_at?: string | null; [key: string]: unknown };

/**
 * Indexes worth having, by entity. Only foreign keys the app actually filters
 * on — an index is a write cost on every sync, so they are added because a
 * screen needs one, not in case.
 */
const INDEXES: Partial<Record<Entity, string[]>> = {
  site: ["customer_id"],
  contact: ["customer_id", "site_id"],
  asset: ["site_id", "installed_by_job_id"],
  asset_event: ["asset_id"],
  job: ["site_id", "customer_id", "assigned_user_id", "status"],
  job_line: ["job_id"],
  time_entry: ["job_id", "user_id"],
  variation: ["job_id"],
  note: ["job_id", "asset_id"],
  attachment: ["job_id", "asset_id"],
  quote: ["site_id", "customer_id"],
  quote_section: ["quote_id"],
  quote_line: ["section_id"],
  kit_line: ["kit_id"],
  stock_level: ["location_id", "catalogue_item_id"],
  form_submission: ["job_id", "asset_id"],
};

let opening: Promise<IDBDatabase> | null = null;

export function open(): Promise<IDBDatabase> {
  if (opening) return opening;

  opening = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      const upgrade = request.transaction;

      for (const entity of ENTITIES) {
        const store = db.objectStoreNames.contains(entity)
          ? upgrade?.objectStore(entity)
          : db.createObjectStore(entity, { keyPath: "id" });
        if (!store) continue;
        for (const field of INDEXES[entity] ?? []) {
          if (!store.indexNames.contains(field)) store.createIndex(field, field, { unique: false });
        }
      }

      if (!db.objectStoreNames.contains(META)) {
        db.createObjectStore(META, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(OUTBOX)) {
        // autoIncrement, so the queue drains in the order the work happened.
        const outbox = db.createObjectStore(OUTBOX, { keyPath: "seq", autoIncrement: true });
        outbox.createIndex("sent_at", "sent_at", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("could not open the local store"));
    request.onblocked = () =>
      reject(new Error("another tab is holding an older version of the local store open"));
  });

  return opening;
}

/** Test seam, and what a sign-out uses. */
export function closeForTests() {
  opening?.then((db) => db.close()).catch(() => {});
  opening = null;
}

function run<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("the write was aborted"));
  });
}

/* --------------------------------------------------------------- reading */

export async function get<T = Row>(entity: Entity, id: string): Promise<T | undefined> {
  const db = await open();
  const row = await run(db.transaction(entity, "readonly").objectStore(entity).get(id));
  return row === undefined || isDeleted(row) ? undefined : (row as T);
}

/**
 * Every live row of an entity.
 *
 * Soft-deleted rows are filtered out rather than removed, because a device
 * that has not synced may still be carrying changes that refer to them, and
 * hard deletes break exactly that case (D2).
 */
export async function all<T = Row>(entity: Entity): Promise<T[]> {
  const db = await open();
  const rows = await run(db.transaction(entity, "readonly").objectStore(entity).getAll());
  return (rows as Row[]).filter((row) => !isDeleted(row)) as T[];
}

/** Live rows whose indexed `field` equals `value`. */
export async function where<T = Row>(entity: Entity, field: string, value: unknown): Promise<T[]> {
  const db = await open();
  const store = db.transaction(entity, "readonly").objectStore(entity);

  if (!store.indexNames.contains(field)) {
    // Falling back to a scan rather than throwing: a missing index is a
    // performance bug on a few hundred rows, not a broken screen. It is worth
    // knowing about, though.
    console.warn(`[store] no index on ${entity}.${field} — scanning`);
    const rows = (await run(store.getAll())) as Row[];
    return rows.filter((row) => !isDeleted(row) && row[field] === value) as T[];
  }

  const rows = (await run(store.index(field).getAll(value as IDBValidKey))) as Row[];
  return rows.filter((row) => !isDeleted(row)) as T[];
}

export async function count(entity: Entity): Promise<number> {
  const db = await open();
  return run(db.transaction(entity, "readonly").objectStore(entity).count());
}

function isDeleted(row: unknown): boolean {
  return Boolean((row as Row | undefined)?.deleted_at);
}

/* --------------------------------------------------------------- writing */

export async function put(entity: Entity, rows: Row | Row[]): Promise<void> {
  const list = Array.isArray(rows) ? rows : [rows];
  if (list.length === 0) return;

  const db = await open();
  const tx = db.transaction(entity, "readwrite");
  const store = tx.objectStore(entity);
  for (const row of list) store.put(row);
  return done(tx);
}

/**
 * Merge fields into a stored row, creating it if it is not there.
 *
 * Field-level on purpose, matching the server's merge (D3): the office
 * retitling a job must not wipe the three parts and a signature the tech added
 * while offline, and a store that writes whole rows makes that promise
 * impossible to keep no matter how careful the sync code is.
 */
export async function patch(entity: Entity, id: string, fields: Record<string, unknown>): Promise<Row> {
  const db = await open();
  const tx = db.transaction(entity, "readwrite");
  const store = tx.objectStore(entity);

  const existing = ((await run(store.get(id))) as Row | undefined) ?? { id };
  const merged: Row = { ...existing, ...fields, id };
  store.put(merged);

  await done(tx);
  return merged;
}

/** Soft delete, for the same reason the server soft deletes. */
export async function remove(entity: Entity, id: string, at = new Date().toISOString()): Promise<void> {
  await patch(entity, id, { deleted_at: at });
}

/**
 * Replace everything in these entities with `rows`, in one transaction.
 *
 * Used by a full hydrate. One transaction across all of them, so a hydrate
 * that fails halfway leaves the previous working set intact rather than a
 * device carrying half of one day and half of another.
 */
export async function replaceAll(payload: Partial<Record<Entity, Row[]>>): Promise<void> {
  const entities = ENTITIES.filter((e) => e in payload);
  if (entities.length === 0) return;

  const db = await open();
  const tx = db.transaction(entities, "readwrite");

  // The try/abort is load-bearing. `put` throws *synchronously* on a row that
  // violates the key path — a malformed record from the server, a field the
  // API renamed — rather than firing an error event. Without this, that throw
  // escapes the loop, nothing aborts the transaction, and it commits anyway:
  // the `clear()` above it lands and the replacement rows do not, leaving the
  // device carrying nothing. Rolling back keeps yesterday's working set, which
  // is stale but coherent, and a screen of stale jobs beats a blank one.
  try {
    for (const entity of entities) {
      const store = tx.objectStore(entity);
      store.clear();
      for (const row of payload[entity] ?? []) store.put(row);
    }
  } catch (cause) {
    tx.abort();
    await done(tx).catch(() => {});
    throw cause;
  }

  return done(tx);
}

/* ------------------------------------------------------------------ meta */

export async function readMeta<T>(key: string, fallback: T): Promise<T> {
  const db = await open();
  const row = (await run(db.transaction(META, "readonly").objectStore(META).get(key))) as
    | { key: string; value: T }
    | undefined;
  return row === undefined ? fallback : row.value;
}

export async function writeMeta<T>(key: string, value: T): Promise<void> {
  const db = await open();
  const tx = db.transaction(META, "readwrite");
  tx.objectStore(META).put({ key, value });
  return done(tx);
}

/** Everything this device is carrying, by entity. For the status surface. */
export async function inventory(): Promise<Record<string, number>> {
  const db = await open();
  const tx = db.transaction(ENTITIES as unknown as string[], "readonly");
  const counts: Record<string, number> = {};
  await Promise.all(
    ENTITIES.map(async (entity) => {
      const n = await run(tx.objectStore(entity).count());
      if (n > 0) counts[entity] = n;
    }),
  );
  return counts;
}

/**
 * Forget everything. What signing out does.
 *
 * A shared van iPad handed to the next tech must not still be carrying the
 * last one's jobs, their customers' access notes and their van's stock.
 */
export async function wipe(): Promise<void> {
  const db = await open();
  const names = [...ENTITIES, META, OUTBOX] as string[];
  const tx = db.transaction(names, "readwrite");
  for (const name of names) tx.objectStore(name).clear();
  return done(tx);
}

export { run as runRequest, done as txDone };
